// Custom hook to handle gesture detection with drag vs click detection
import { useRef } from 'react'
import { useDrag, useGesture } from '@use-gesture/react'
import { GestureHandlers, GestureOptions } from '../types/gesture-types'

export const DRAG_THRESHOLD = 10 // Threshold to make it harder to trigger accidental taps

// Add type definitions for our custom properties on events
declare global {
  // Augment the Event interfaces to add our custom property
  interface Event {
    __wasDrag?: boolean
  }
}

/**
 * Custom hook that provides gesture bindings with proper drag detection
 *
 * @param handlers Object containing handlers for gesture events
 * @param options Configuration options for the gesture detection
 * @returns Binding object to spread on a DOM element
 */
export function useGestureWithDragDetection(handlers: GestureHandlers, options: GestureOptions = {}) {
  const { onDragStart, onDragMove, onDragEnd, onTap } = handlers
  const { dragThreshold = DRAG_THRESHOLD, axis = 'x' } = options

  const isDraggingRef = useRef<boolean>(false)
  const hasMovedRef = useRef<boolean>(false)
  const dragDistanceRef = useRef<number>(0)

  // For drag-only gestures
  const useDragBind = useDrag(
    ({ first, active, movement, last, event, distance }) => {
      if (first) {
        isDraggingRef.current = true
        hasMovedRef.current = false
        dragDistanceRef.current = 0
        onDragStart?.({
          event,
          deltaX: movement[0],
          deltaY: movement[1],
        })
      }

      if (active) {
        // Calculate distance based on the axis
        const relevantDistance = axis === 'y' ? Math.abs(distance[1]) : Math.abs(distance[0])

        dragDistanceRef.current = relevantDistance

        // If we've moved more than the threshold, mark it as a real drag
        if (dragDistanceRef.current > dragThreshold) {
          hasMovedRef.current = true
        }

        onDragMove?.({
          event,
          deltaX: movement[0],
          deltaY: movement[1],
        })
      }

      if (last) {
        // We're no longer dragging
        const wasDragging = isDraggingRef.current
        const hasDragged = hasMovedRef.current

        isDraggingRef.current = false
        onDragEnd?.({
          event,
          deltaX: movement[0],
          deltaY: movement[1],
        })

        // Store drag state for click handler to use
        if (event && typeof event === 'object') {
          // Typescript-safe way to add property
          ;(event as any).__wasDrag = wasDragging && hasDragged
        }
      }
    },
    {
      axis, // track movement based on specified axis
      threshold: 0, // no threshold to trigger the gesture
    }
  )

  // For full gesture handling including taps/clicks
  const useGestureBind = useGesture(
    {
      onDrag: ({ first, active, movement, last, event, distance }) => {
        if (first) {
          isDraggingRef.current = true
          hasMovedRef.current = false
          dragDistanceRef.current = 0
          onDragStart?.({
            event,
            deltaX: movement[0],
            deltaY: movement[1],
          })
        }

        if (active) {
          // Calculate distance based on the axis
          const relevantDistance = axis === 'y' ? Math.abs(distance[1]) : Math.abs(distance[0])

          dragDistanceRef.current = relevantDistance

          // If we've moved more than the threshold, mark it as a real drag
          if (dragDistanceRef.current > dragThreshold) {
            hasMovedRef.current = true
          }

          onDragMove?.({
            event,
            deltaX: movement[0],
            deltaY: movement[1],
          })
        }

        if (last) {
          // We're no longer dragging
          const wasDragging = isDraggingRef.current
          const hasDragged = hasMovedRef.current

          isDraggingRef.current = false
          onDragEnd?.({
            event,
            deltaX: movement[0],
            deltaY: movement[1],
          })

          // Store drag state for click handler to use
          if (event && typeof event === 'object') {
            // Typescript-safe way to add property
            ;(event as any).__wasDrag = wasDragging && hasDragged
          }
        }
      },
      onClick: ({ event }) => {
        // Don't trigger click if we just finished a real drag operation
        const wasDrag = event ? (event as any).__wasDrag : false

        if (!wasDrag && onTap && !isDraggingRef.current) {
          // Small delay to make sure we don't have race conditions with drag events
          setTimeout(() => {
            // Extra check to make sure we're not in a drag state
            if (!isDraggingRef.current && !hasMovedRef.current) {
              onTap({ event })
            }
          }, 0)
        }
      },
    },
    {
      drag: {
        axis, // track movement based on specified axis
        threshold: 0, // no threshold to trigger the gesture
      },
    }
  )

  // Return appropriate bind function based on whether tap is needed
  return onTap ? useGestureBind : useDragBind
}
